/** One soldier on screen: a single rigid-skinned mesh, texel-locked, posed procedurally (05 §6). */

import { Bone, type Material, Skeleton, SkinnedMesh, Vector3 } from 'three';
import type { PixelCamera } from '../camera/PixelCamera';
import type { Palette } from '../palette';
import { buildGeometry, type SoldierLook } from './model';
import { applyPose, type Motion, type PoseInput } from './poses';
import { B, BONE_COUNT, REST } from './rig';

export type { Side, SoldierLook } from './model';
export type { Motion } from './poses';

/** Characters are drawn a little larger than life so heads and helmets read (05 §3). */
export const CHARACTER_SCALE = 1.18;

/** Limb poses update "on twos", like hand-animated sprites; movement stays smooth (05 §6.2). */
export const POSE_HZ = 12;

/** Rendered facing snaps to this many directions; the sim keeps the true angle. */
export const FACING_STEPS = 16;

/** Everything a pose needs (see `PoseInput`), plus where he stands. Overlays default to "none". */
export interface SoldierState extends Required<PoseInput> {
  /** World position of the feet. */
  position: Vector3;
  /** Heading in radians; 0 faces south (toward the camera), PI faces north. */
  facing: number;
  motion: Motion;
}

/**
 * One soldier: a single rigid-skinned mesh (one draw call) posed procedurally every frame.
 * There are no keyframes anywhere - a gait is a few phase-shifted sines (05 §6.2).
 */
export class Soldier {
  readonly mesh: SkinnedMesh;
  readonly state: SoldierState = {
    position: new Vector3(), facing: 0, motion: 'idle', travelled: 0, twist: 0, reverse: false,
    age: 0, kick: 0, flinch: 0, handsUp: false, vigour: 1,
  };
  private readonly bones: Bone[] = [];
  private readonly snapped = new Vector3();

  constructor(palette: Palette, material: Material, look: SoldierLook) {
    for (let i = 0; i < BONE_COUNT; i++) {
      const bone = new Bone();
      const rest = REST[i];
      if (rest) {
        bone.position.set(rest[1], rest[2], rest[3]);
        const parent = this.bones[rest[0]];
        if (parent) parent.add(bone);
      }
      this.bones.push(bone);
    }
    const root = this.bone(B.root);
    root.updateMatrixWorld(true);
    this.mesh = new SkinnedMesh(buildGeometry(palette, look), material);
    this.mesh.add(root);
    this.mesh.bind(new Skeleton(this.bones));
    this.mesh.scale.setScalar(CHARACTER_SCALE);
    this.mesh.castShadow = true;
    this.mesh.receiveShadow = true;
    this.mesh.frustumCulled = false;
  }

  private bone(index: number): Bone {
    const b = this.bones[index];
    if (!b) throw new Error(`no bone ${index}`);
    return b;
  }

  /**
   * Dress him differently - the ghillie wrap on or off - on the same rig: the bones and the draw call stay,
   * only the geometry bound to them is swapped (and the old one freed).
   */
  redress(palette: Palette, look: SoldierLook): void {
    const old = this.mesh.geometry;
    this.mesh.geometry = buildGeometry(palette, look);
    old.dispose();
  }

  /** Put every overlay and one-shot clock back to "none": call before choosing this frame's pose. */
  clearOverlays(): void {
    const s = this.state;
    s.age = 0;
    s.kick = 0;
    s.flinch = 0;
    s.handsUp = false;
    s.vigour = 1;
  }

  /** Apply state to the mesh. `time` is seconds; poses are evaluated on the POSE_HZ grid. */
  update(time: number, camera: PixelCamera): void {
    const s = this.state;
    camera.snapToTexel(s.position, this.snapped);
    this.mesh.position.copy(this.snapped);
    const step = (Math.PI * 2) / FACING_STEPS;
    this.mesh.rotation.y = Math.round(s.facing / step) * step;
    applyPose(this.bones, (i) => this.bone(i), s, Math.floor(time * POSE_HZ) / POSE_HZ);
  }
}
